import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import API from '../AgenticScreening/API';
import StyledModal from '../Components/GenericModal/StyledModal';
import MultiSelectWithCreate from '../Components/CustomUIElements/MultiSelectWithCreate';
import ModalFooterButtons from '../Components/GenericModal/ModalFooterButtons';
import { ICandidateGroup } from '../AgenticMatching/types';
import SelectedCandidates from './SelectedCandidates';

const AddToCandidatePool = ({
  isOpen,
  handleClose,
  selectedCandidateGroups,
  onRemoveCandidateGroup,
  refetch
}: {
  isOpen: boolean;
  handleClose: () => void;
  selectedCandidateGroups: ICandidateGroup[];
  onRemoveCandidateGroup: (candidateGroupId: number) => void;
  refetch?: () => void;
}) => {
  const invalidCandidateGroups = selectedCandidateGroups.filter(
    (candidateGroup) => candidateGroup.emails.length === 0
  );
  const [selectedPools, setSelectedPools] = useState<any[]>([]);
  const selectedCandidateGroupIds = selectedCandidateGroups
    ? selectedCandidateGroups.map((candidateGroup) => candidateGroup.id)
    : [];

  const { data: pools, isLoading: isLoadingPools } = useQuery({
    queryKey: ['candidatePoolsList'],
    queryFn: async () => {
      const res = await API.getCandidatePools();
      return (res.res as any).pools;
    },
    enabled: isOpen
  });

  const { mutate: createCandidatePool, isLoading: creatingPool } = useMutation({
    mutationFn: async (data: string) => {
      const res = await API.createCandidatePool(data);
      setSelectedPools([...selectedPools, res.res as any]);
    }
  });

  const { mutate: addCandidatesToPools, isLoading: addingCandidates } = useMutation({
    mutationFn: async () => {
      await API.addCandidatesToPools(
        selectedCandidateGroupIds,
        selectedPools.map((pool) => pool.id)
      );
      handleClose();
      refetch && refetch();
    }
  });

  const handlePoolsChange = (value: any[]) => {
    if (typeof value.at(-1) === 'string') {
      createCandidatePool(value.at(-1) as string);
    } else {
      setSelectedPools(value);
    }
  };

  const submitDisabled =
    selectedPools.length === 0 || addingCandidates || invalidCandidateGroups.length > 0;

  return (
    <StyledModal
      isOpen={isOpen}
      handleClose={handleClose}
      styleOverrides={{
        maxWidth: '830px',
        maxHeight: '100vh'
      }}
      label="add-to-candidate-pool"
      modalTitle={'Add candidates to pools'}
      subHeaderStyling={false}
    >
      <Box
        sx={{
          fontFamily: 'Source Sans Pro',
          fontSize: '16px',
          color: '#939393',
          width: '100%',
          paddingInline: '2px',
          marginTop: '16px'
        }}
      >
        Select or create candidate pools
        <MultiSelectWithCreate
          dropdownOptions={pools || []}
          onChange={(newValues) => handlePoolsChange(newValues)}
          values={selectedPools}
          loadingOptions={isLoadingPools}
          creatingOption={creatingPool}
        />
      </Box>
      <SelectedCandidates
        handleClose={handleClose}
        selectedCandidateGroups={selectedCandidateGroups}
        onRemoveCandidateGroup={onRemoveCandidateGroup}
        invalidCandidateGroups={invalidCandidateGroups}
      />
      <ModalFooterButtons
        primaryButtonText="Add to pools"
        primaryButtonCallback={addCandidatesToPools}
        primaryButtonDisabled={submitDisabled}
        secondaryButtonText="Cancel"
        secondaryButtonCallback={handleClose}
        isLoading={addingCandidates}
        primaryButtonID="add-candidates-button"
        secondaryButtonID="cancel-add-to-pool"
      />
    </StyledModal>
  );
};

export default AddToCandidatePool;
